import {
  Contact,
  ContactMetadata,
  ContactType,
  ContactUnitLink,
  IAddress,
} from '../../src/app/shared/models/entity';
import {
  auditFields,
  createMany,
  cycle,
  FixtureBuilder,
  FixtureFactory,
  fixtureId,
} from './fixture.utils';

// -----------------------------------------------------------------------------------------------------
// @ SAMPLE POOLS
// -----------------------------------------------------------------------------------------------------

const PERSON_NAMES: readonly string[] = [
  'Anna Weber',
  'Michael Schneider',
  'Julia Hoffmann',
  'Thomas Krüger',
  'Sabine Richter',
  'Daniel Wagner',
  'Claudia Neumann',
  'Stefan Braun',
];

const COMPANY_NAMES: readonly string[] = [
  'Hausmeister Service Nord GmbH',
  'Elektro Lindner KG',
  'Grünflächen Berlin GmbH',
  'Aufzugtechnik Meier AG',
];

const STREETS: readonly string[] = [
  'Lindenstraße',
  'Goethestraße',
  'Bergmannstraße',
  'Kastanienallee',
];

const CITIES: readonly [string, string][] = [
  ['10115', 'Berlin'],
  ['20095', 'Hamburg'],
  ['80331', 'München'],
  ['50667', 'Köln'],
];

// -----------------------------------------------------------------------------------------------------
// @ FACTORY
// -----------------------------------------------------------------------------------------------------

/**
 * Builds a Contact with realistic defaults. Every field can be overridden.
 *
 * @param overrides - The fields to override.
 * @param index - The zero-based index, driving the deterministic id and the sample rotation.
 * @returns {Contact} A fresh Contact instance.
 */
export const createContact: FixtureFactory<Contact> = (
  overrides: Partial<Contact> = {},
  index: number = 0,
): Contact => {
  const name: string = cycle(PERSON_NAMES, index);
  const [zipCode, city] = cycle(CITIES, index);

  return new Contact({
    id: fixtureId('contact', index),
    ...auditFields(index),
    name,
    type: 'Person',
    email: toEmail(name),
    phoneNumber: `+49 30 ${(1000000 + index * 7919).toString().slice(0, 7)}`,
    street: cycle(STREETS, index),
    houseNumber: `${(index % 40) + 1}`,
    zipCode,
    city,
    isAdvisoryBoard: false,
    metadata: new ContactMetadata(),
    units: [],
    ...overrides,
  });
};

/**
 * Builds a list of Contacts.
 *
 * @param count - How many contacts to build.
 * @param overrides - Static overrides, or a callback returning overrides per index.
 * @returns {Contact[]} The built contacts.
 */
export function createContacts(
  count: number,
  overrides?: Partial<Contact> | ((index: number) => Partial<Contact>),
): Contact[] {
  return createMany(createContact, count, overrides);
}

// -----------------------------------------------------------------------------------------------------
// @ BASE FIXTURES
// -----------------------------------------------------------------------------------------------------

export const DEFAULT_CONTACT: Contact = createContact();

// -----------------------------------------------------------------------------------------------------
// @ SCENARIO FIXTURES
// -----------------------------------------------------------------------------------------------------

/** An owner who also sits on the Verwaltungsbeirat. */
export const CONTACT_ADVISORY_BOARD: Contact = createContact({ isAdvisoryBoard: true }, 1);

/** A service provider — a company, not a natural person. */
export const CONTACT_COMPANY: Contact = createContact(
  {
    name: COMPANY_NAMES[0],
    type: 'Company',
    email: 'kontakt@hausmeister-nord.de',
  },
  2,
);

/** An incomplete record — useful for empty-state and validation paths. */
export const CONTACT_WITHOUT_CONTACT_DETAILS: Contact = createContact(
  { email: '', phoneNumber: '' },
  3,
);

// -----------------------------------------------------------------------------------------------------
// @ BUILDER
// -----------------------------------------------------------------------------------------------------

export class ContactBuilder extends FixtureBuilder<Contact> {
  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(index: number = 0) {
    super(createContact({}, index));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Turns the contact into a company, e.g. a service provider.
   *
   * @param name - The company name. Defaults to the next name from the company pool.
   * @returns {this} The builder, for chaining.
   */
  asCompany(name?: string): this {
    const companyName: string = name ?? cycle(COMPANY_NAMES, 0);

    return this.with({
      type: 'Company' as ContactType,
      name: companyName,
      email: toEmail(companyName),
    });
  }

  /**
   * Marks the contact as a member of the Verwaltungsbeirat.
   *
   * @returns {this} The builder, for chaining.
   */
  asAdvisoryBoard(): this {
    return this.with({ isAdvisoryBoard: true });
  }

  /**
   * Sets the postal address of the contact.
   *
   * @param address - The address fields to apply.
   * @returns {this} The builder, for chaining.
   */
  withAddress(address: Partial<IAddress>): this {
    return this.with(address as Partial<Contact>);
  }

  /**
   * Attaches the unit links of the contact — the detail projection's flat link shape
   * (unit + role + property context), not full Unit graphs.
   *
   * @param units - The unit links to attach.
   * @returns {this} The builder, for chaining.
   */
  withUnits(units: ContactUnitLink[]): this {
    return this.with({ units });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ OVERRIDE METHODS
  // -----------------------------------------------------------------------------------------------------

  override build(): Contact {
    return new Contact(this.draft);
  }
}

// -----------------------------------------------------------------------------------------------------
// @ PRIVATE
// -----------------------------------------------------------------------------------------------------

/**
 * Derives a plausible email address from a display name.
 *
 * @param name - The display name.
 * @returns {string} The derived email address.
 */
function toEmail(name: string): string {
  const local: string = name
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.|\.$/g, '');

  return `${local}@example.de`;
}
